import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { twinApi, assetsApi, type TwinProject } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'

type UploadState = 'idle' | 'creating' | 'uploading' | 'processing' | 'complete' | 'error'

const ACCEPTED_FORMATS = [
  '.step', '.stp',
  '.iges', '.igs',
  '.stl',
  '.obj',
  '.gltf', '.glb',
]

export function TwinProjectCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [projectName, setProjectName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedAssetId, setSelectedAssetId] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [createdProject, setCreatedProject] = useState<TwinProject | null>(null)

  // Fetch available assets (unlinked ones for dropdown)
  const { data: assetsResponse } = useQuery({
    queryKey: ['assets', 'unlinked'],
    queryFn: async () => {
      const response = await assetsApi.list({ limit: 100 })
      // Filter to assets without a twin project linked
      return response.data?.filter(a => !a.twinProjectId) || []
    },
  })

  const availableAssets = assetsResponse || []

  const handleFileSelect = useCallback((file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ACCEPTED_FORMATS.includes(extension)) {
      setErrorMessage(t('twin.upload.failed') + ` Supported: ${ACCEPTED_FORMATS.join(', ')}`)
      return
    }
    setSelectedFile(file)
    setErrorMessage(null)
    setUploadState('idle')

    // Auto-fill project name from filename if empty
    if (!projectName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
      setProjectName(nameWithoutExt)
    }
  }, [projectName, t])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const clearFile = useCallback(() => {
    setSelectedFile(null)
    setUploadState('idle')
    setErrorMessage(null)
    setProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || !projectName.trim()) {
      setErrorMessage('Please enter a project name and select a CAD file')
      return
    }

    try {
      // Step 1: Create Twin project
      setUploadState('creating')
      setProgress(10)
      setErrorMessage(null)

      const project = await twinApi.createProject({
        name: projectName.trim(),
        description: description.trim() || undefined,
        metadata: { source: 'manual-upload' },
      })

      setCreatedProject(project)

      // Step 2: Upload CAD file
      setUploadState('uploading')
      setProgress(30)

      await twinApi.uploadCAD(project.id, selectedFile)

      // Step 3: Monitor conversion
      setUploadState('processing')
      setProgress(60)

      // Poll for conversion status
      let attempts = 0
      const maxAttempts = 60 // 5 minutes max
      const pollInterval = 5000 // 5 seconds

      const checkStatus = async (): Promise<TwinProject> => {
        const status = await twinApi.getConversionStatus(project.id)

        if (status.status === 'completed') {
          setProgress(100)
          const updatedProject = await twinApi.getProject(project.id)
          return updatedProject
        }

        if (status.status === 'failed') {
          throw new Error(status.error || 'Conversion failed')
        }

        attempts++
        if (attempts >= maxAttempts) {
          throw new Error('Conversion timed out')
        }

        // Update progress based on conversion progress
        if (status.progress) {
          setProgress(60 + Math.floor(status.progress * 0.4))
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval))
        return checkStatus()
      }

      const finalProject = await checkStatus()

      // Step 4: Link to asset if selected
      if (selectedAssetId) {
        await assetsApi.update(selectedAssetId, { twinProjectId: finalProject.id })
      }

      setUploadState('complete')
      setCreatedProject(finalProject)

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['twin-projects'] })
      queryClient.invalidateQueries({ queryKey: ['assets'] })

      // Redirect to detail page after a short delay
      setTimeout(() => {
        navigate(`/twin/${finalProject.id}`)
      }, 1500)

    } catch (error) {
      console.error('[TwinProjectCreate] Error:', error)
      setUploadState('error')
      const message = error instanceof Error ? error.message : 'Upload failed'
      setErrorMessage(message)
    }
  }, [selectedFile, projectName, description, selectedAssetId, queryClient, navigate])

  const handleRetry = useCallback(() => {
    setUploadState('idle')
    setErrorMessage(null)
    setProgress(0)
  }, [])

  const isSubmitting = uploadState !== 'idle' && uploadState !== 'error' && uploadState !== 'complete'
  const canSubmit = projectName.trim() && selectedFile && !isSubmitting

  return (
    <div className="p-4 lg:p-8">
      <div className="mx-auto max-w-3xl">
        {/* Back link */}
        <div className="mb-6">
          <Link
            to="/twin"
            className="inline-flex items-center gap-x-2 text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="size-4" />
            {t('twin.projects.title')}
          </Link>
        </div>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('twin.upload.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('twin.upload.description')}</p>
        </div>

        <div className="mt-8 space-y-6">
          {/* Project Name */}
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700">
              {t('twin.upload.projectName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              disabled={isSubmitting}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder={t('twin.upload.projectName')}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              {t('common.description')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Optional description..."
            />
          </div>

          {/* Asset Selection */}
          <div>
            <label htmlFor="asset" className="block text-sm font-medium text-gray-700">
              {t('twin.upload.selectAsset')}
            </label>
            <select
              id="asset"
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
              disabled={isSubmitting}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{t('twin.upload.noAsset')}</option>
              {availableAssets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.serialNumber} {asset.model ? `- ${asset.model}` : ''} {asset.account?.name ? `(${asset.account.name})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Only showing assets without a linked 3D model
            </p>
          </div>

          {/* File Upload Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CAD File <span className="text-red-500">*</span>
            </label>

            {!selectedFile ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                className="flex justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10 hover:border-brand-400 transition-colors cursor-pointer"
              >
                <div className="text-center">
                  <CloudArrowUpIcon className="mx-auto size-12 text-gray-400" aria-hidden="true" />
                  <div className="mt-4 flex text-sm text-gray-600">
                    <span className="font-semibold text-brand-600 hover:text-brand-500">
                      {t('twin.upload.dropzone')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{t('twin.upload.formats')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FORMATS.join(',')}
                    onChange={handleFileInputChange}
                    className="sr-only"
                  />
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <DocumentIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  {uploadState === 'idle' && (
                    <button
                      type="button"
                      onClick={clearFile}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Progress bar */}
                {(uploadState === 'creating' || uploadState === 'uploading' || uploadState === 'processing') && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>
                        {uploadState === 'creating' && 'Creating project...'}
                        {uploadState === 'uploading' && t('twin.upload.uploading')}
                        {uploadState === 'processing' && t('twin.upload.converting')}
                      </span>
                      <span>{progress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Complete state */}
                {uploadState === 'complete' && (
                  <div className="mt-4 flex items-center gap-2 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('twin.upload.complete')}</span>
                  </div>
                )}

                {/* Error state */}
                {uploadState === 'error' && (
                  <div className="mt-4 flex items-center gap-2 text-red-600">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span className="text-sm">{errorMessage}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error message (for validation errors) */}
          {errorMessage && uploadState === 'idle' && (
            <div className="flex items-center gap-2 text-red-600">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <p className="text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-x-3 pt-4 border-t border-gray-200">
            <Link
              to="/twin"
              className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </Link>

            {uploadState === 'error' ? (
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-600"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t('twin.viewer.tryAgain')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    {uploadState === 'creating' && 'Creating...'}
                    {uploadState === 'uploading' && t('twin.upload.uploading')}
                    {uploadState === 'processing' && t('twin.upload.converting')}
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="h-4 w-4" />
                    {t('twin.projects.create')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
